import type { ChannelShareData } from "@expert/sales-tools";

import { useCallDetailsStore } from "@expert/sales-tools";
import { Button, Chip, Divider, Group, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

const IVR_CSD_KEYS: (keyof ChannelShareData)[] = [
    "INTENT",
    "SUMMARY",
    "CONVERSATIONAL_SUMMARY",
    "CUSTOMER_NAME",
    "PRODUCT_LINK",
    "PRODUCT_TITLE",
    "ORDER_NUMBER",
    "BRAND_PHONE_NUMBER",
    "SUPPORT_PHONE_NUMBER",
];

const STANDARD_PRESET = {
    INTENT: "Screen repair",
    SUMMARY:
        "Customer called about a cracked screen on their device. They dropped it yesterday and the display is flickering.",
    CUSTOMER_NAME: "Jane Smith",
    PRODUCT_LINK: "https://example.com/screen-protector",
    PRODUCT_TITLE: "Galaxy S24 Ultra",
    ORDER_NUMBER: "202-6987651-2323122",
    BRAND_PHONE_NUMBER: "8005551234",
    SUPPORT_PHONE_NUMBER: "8005554321",
};

const CONVERSATIONAL_PRESET = {
    INTENT: "Screen repair",
    CONVERSATIONAL_SUMMARY:
        "I see you're calling about your Galaxy S24 screen. It looks like you mentioned it cracked after a drop — I'm sorry to hear that! Let me pull up your protection plan and we'll get this sorted out.",
    CUSTOMER_NAME: "Jane Smith",
    PRODUCT_TITLE: "Galaxy S24 Ultra",
};

const EMIT_SUCCESS_DURATION_MS = 2000;

export function IVRTab() {
    const [mode, setMode] = useState<"standard" | "conversational">("standard");
    const [intent, setIntent] = useState(STANDARD_PRESET.INTENT);
    const [summary, setSummary] = useState(STANDARD_PRESET.SUMMARY);
    const [conversationalSummary, setConversationalSummary] = useState("");
    const [customerName, setCustomerName] = useState(STANDARD_PRESET.CUSTOMER_NAME);
    const [productLink, setProductLink] = useState(STANDARD_PRESET.PRODUCT_LINK);
    const [productTitle, setProductTitle] = useState(STANDARD_PRESET.PRODUCT_TITLE);
    const [orderNumber, setOrderNumber] = useState(STANDARD_PRESET.ORDER_NUMBER);
    const [brandPhoneNumber, setBrandPhoneNumber] = useState(STANDARD_PRESET.BRAND_PHONE_NUMBER);
    const [supportPhoneNumber, setSupportPhoneNumber] = useState(STANDARD_PRESET.SUPPORT_PHONE_NUMBER);
    const [showEmitted, setShowEmitted] = useState(false);

    const canEmit = intent || summary || conversationalSummary;

    useEffect(() => {
        if (!showEmitted) return;
        const id = setTimeout(() => setShowEmitted(false), EMIT_SUCCESS_DURATION_MS);
        return () => clearTimeout(id);
    }, [showEmitted]);

    const applyPreset = (preset: Record<string, string>) => {
        setIntent(preset.INTENT ?? "");
        setSummary(preset.SUMMARY ?? "");
        setConversationalSummary(preset.CONVERSATIONAL_SUMMARY ?? "");
        setCustomerName(preset.CUSTOMER_NAME ?? "");
        setProductLink(preset.PRODUCT_LINK ?? "");
        setProductTitle(preset.PRODUCT_TITLE ?? "");
        setOrderNumber(preset.ORDER_NUMBER ?? "");
        setBrandPhoneNumber(preset.BRAND_PHONE_NUMBER ?? "");
        setSupportPhoneNumber(preset.SUPPORT_PHONE_NUMBER ?? "");
        setMode(preset.CONVERSATIONAL_SUMMARY ? "conversational" : "standard");
    };

    const inject = useCallback(() => {
        if (!canEmit) return;
        const { callDetails } = useCallDetailsStore.getState();
        const ivrFields: Partial<ChannelShareData> = {
            INTENT: intent || undefined,
            SUMMARY: mode === "standard" ? summary || undefined : undefined,
            CONVERSATIONAL_SUMMARY: mode === "conversational" ? conversationalSummary || undefined : undefined,
            CUSTOMER_NAME: customerName || undefined,
            PRODUCT_LINK: mode === "standard" ? productLink || undefined : undefined,
            PRODUCT_TITLE: productTitle || undefined,
            ORDER_NUMBER: orderNumber || undefined,
            BRAND_PHONE_NUMBER: brandPhoneNumber || undefined,
            SUPPORT_PHONE_NUMBER: supportPhoneNumber || undefined,
        };
        useCallDetailsStore.getState().setCallDetails({ ...callDetails, ...ivrFields });
        setShowEmitted(true);
    }, [
        canEmit,
        intent,
        summary,
        conversationalSummary,
        customerName,
        productLink,
        productTitle,
        orderNumber,
        brandPhoneNumber,
        supportPhoneNumber,
        mode,
    ]);

    const reset = useCallback(() => {
        const { callDetails } = useCallDetailsStore.getState();
        const cleaned: ChannelShareData = { ...callDetails };
        for (const key of IVR_CSD_KEYS) {
            delete cleaned[key];
        }
        useCallDetailsStore.getState().setCallDetails(cleaned);
        setIntent("");
        setSummary("");
        setConversationalSummary("");
        setCustomerName("");
        setProductLink("");
        setProductTitle("");
        setOrderNumber("");
        setBrandPhoneNumber("");
        setSupportPhoneNumber("");
        setShowEmitted(true);
    }, []);

    return (
        <Stack gap="sm">
            <Divider label="Presets" labelPosition="left" />

            <Group gap="xs">
                <Button size="xs" variant="light" color="violet" onClick={() => applyPreset(STANDARD_PRESET)}>
                    Standard
                </Button>
                <Button size="xs" variant="light" color="violet" onClick={() => applyPreset(CONVERSATIONAL_PRESET)}>
                    Conversational
                </Button>
            </Group>

            <Divider label="Mode" labelPosition="left" />

            <Group gap="xs">
                <Chip size="xs" variant="filled" checked={mode === "standard"} onChange={() => setMode("standard")}>
                    Standard IVR
                </Chip>
                <Chip
                    size="xs"
                    variant="filled"
                    checked={mode === "conversational"}
                    onChange={() => setMode("conversational")}
                >
                    Conversational
                </Chip>
            </Group>

            <Divider label="IVR Fields" labelPosition="left" />

            <TextInput
                label="Intent"
                placeholder="e.g. Screen repair"
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                size="xs"
            />

            <TextInput
                label="Customer Name"
                placeholder="e.g. Jane Smith"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                size="xs"
            />

            {mode === "standard" ? (
                <>
                    <Textarea
                        label="Summary"
                        placeholder="Markdown-rendered summary text..."
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        size="xs"
                        autosize
                        minRows={2}
                    />
                    <TextInput
                        label="Product Link"
                        placeholder="https://..."
                        value={productLink}
                        onChange={(e) => setProductLink(e.target.value)}
                        size="xs"
                    />
                </>
            ) : (
                <Textarea
                    label="Conversational Summary"
                    placeholder="Greeting script for the expert..."
                    value={conversationalSummary}
                    onChange={(e) => setConversationalSummary(e.target.value)}
                    size="xs"
                    autosize
                    minRows={3}
                />
            )}

            <Divider label="Device Insights" labelPosition="left" />

            <TextInput
                label="Product Title"
                placeholder="e.g. Galaxy S24 Ultra"
                value={productTitle}
                onChange={(e) => setProductTitle(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Order Number"
                placeholder="e.g. ORD-2026-98765"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Brand Phone Number"
                placeholder="e.g. 8005551234"
                value={brandPhoneNumber}
                onChange={(e) => setBrandPhoneNumber(e.target.value)}
                size="xs"
            />
            <TextInput
                label="Support Phone Number"
                placeholder="e.g. 8005554321"
                value={supportPhoneNumber}
                onChange={(e) => setSupportPhoneNumber(e.target.value)}
                size="xs"
            />

            <Group gap="xs" align="center">
                <Button size="xs" variant="filled" color="violet" onClick={inject} disabled={!canEmit}>
                    Inject IVR Data
                </Button>
                <Button size="xs" variant="light" color="red.5" onClick={reset}>
                    Reset
                </Button>
                {showEmitted && (
                    <Text size="xs" c="green.3" fw={500}>
                        Injected
                    </Text>
                )}
            </Group>
        </Stack>
    );
}
